/**
 * NOLAS 2026 (c) Mika (Mikael FOURNY).
 * src/i18n/fr.donnees.ts
 *
 * Clés de l'accès aux badges et aux CV, du journal de consultation et des
 * purges.
 *
 * Pourquoi un sous-catalogue plutôt que `fr.ts` :
 *   `fr.ts` est LE point de contact de tous les lots. Au lot précédent il
 *   avait été GELÉ, et chacun avait dû SIGNALER ses clés au lieu de les
 *   écrire : 47 clés à reporter à la main après la fusion, et des tests qui
 *   finissaient par contredire leur propre rapport. Cette fois chaque lot a
 *   son fichier, il y écrit librement, et `fr.ts` ne bouge plus.
 *
 * Vocabulaire : aucune de ces chaînes n'écrit en dur le mot qui désigne
 * l'événement, ses ateliers, les entreprises présentes ou les personnes
 * inscrites. Le journal ci-dessous parle d'ENTREPRISES, ce qui est à la fois
 * exact au sens du règlement (le destinataire, c'est l'entreprise, jamais la
 * personne qui a cliqué) et neutre pour les autres instances de la
 * plateforme. Les jetons {eventType*} / {lexWorkshop*} restent disponibles si
 * une clé future en a besoin.
 *
 * Mikael - chef de projet - 2026-09-09
 */
export const messagesDonneesFr: Record<string, string> = {
  // ── « Qui a consulté mon CV ? » (droit d'accès, RGPD art. 15) ───────────
  // Le journal existait en base depuis le premier jour, mais aucun écran ne
  // le lisait : la question ne pouvait tout simplement pas recevoir de
  // réponse.
  'monProfil.cv.journal.title': 'Qui a consulté mon CV ?',
  'monProfil.cv.journal.intro':
    'Chaque consultation et chaque téléchargement de votre CV par une entreprise est enregistré. Vous pouvez les voir ici à tout moment.',
  'monProfil.cv.journal.show': 'Voir qui a consulté mon CV',
  'monProfil.cv.journal.hide': 'Masquer la liste',
  'monProfil.cv.journal.loading': 'Chargement de la liste…',
  'monProfil.cv.journal.empty': 'Personne n’a encore consulté votre CV.',
  'monProfil.cv.journal.emptyNoCv':
    'Vous n’avez pas encore déposé de CV : il n’y a donc rien à consulter.',
  'monProfil.cv.journal.error':
    'La liste n’a pas pu être chargée. Réessayez dans un instant.',
  'monProfil.cv.journal.retry': 'Réessayer',
  'monProfil.cv.journal.companyUnknown': 'Entreprise non identifiée',
  'monProfil.cv.journal.action.view': 'a consulté votre fiche',
  'monProfil.cv.journal.action.download': 'a téléchargé votre CV',
  'monProfil.cv.journal.count':
    '{count, plural, =0 {Aucun accès enregistré} one {# accès enregistré} other {# accès enregistrés}}',
  // Précision utile : sans elle, on pourrait croire qu'un nom absent signifie
  // « accès anonyme », alors que c'est un choix de conception assumé.
  'monProfil.cv.journal.note':
    'Seul le nom de l’entreprise est enregistré, jamais celui de la personne qui a effectué la consultation.',

  // ── Dépôt de CV et personnes mineures (réglage `cv.minors_blocked`) ─────
  // Le message que voit la personne quand l'organisation a activé le
  // réglage. Il dit ce qui se passe ET vers qui se tourner : un refus sans
  // issue est pire qu'un refus.
  'monProfil.cv.error.minorBlocked':
    'Le dépôt de CV n’est pas ouvert aux personnes mineures sur cet espace. Rapprochez-vous de l’organisation, qui pourra vous indiquer la marche à suivre.',

  // ── Le même réglage, côté administration ───────────────────────────────
  // Les deux options décrivent la CONSÉQUENCE de chaque valeur. Un intitulé
  // qui décrit l'état au repos (« autorisation parentale ») se lit à
  // contresens dès qu'on le coche.
  'settingsAdmin.cvMinorsBlocked.label':
    'Dépôt de CV par les personnes mineures',
  'settingsAdmin.cvMinorsBlocked.off':
    'Autorisé (valeur par défaut) : une personne mineure peut déposer son CV',
  'settingsAdmin.cvMinorsBlocked.on':
    'Refusé : une personne mineure ne peut pas déposer ni partager son CV',
  'settingsAdmin.cvMinorsBlocked.help':
    'Désactivé par défaut. Une organisation qui dispose déjà d’une autorisation parentale, par exemple une convention signée avec les familles, n’a pas à l’activer. Activé, le dépôt et l’activation du partage sont refusés aux comptes marqués mineurs, avec un message explicite ; le retrait du partage reste toujours possible.',
};
